import { Alert, Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import type { Insight } from '@/lib/api/analytics';
import { formatRange } from '@/lib/format';
import { DataQualityBadge } from './states';
import { config, type DataOrigin } from '@/config/env';
import { MetricBasisChips } from './MetricBasisChips';
import { ocenInsight, type KandydatSuperlatywu } from '@/lib/superlatyw';
import { useOpisZrodla } from '@/lib/useTrybZrodla';

/** Krótka etykieta na kartę — pełne zdanie idzie do tooltipa. */
const SOURCE_LABEL: Record<DataOrigin, string> = {
  'controlled-test-dataset': 'dane kontrolowane',
  'test-environment': 'środowisko TEST',
  production: 'dane produkcyjne',
  unconfigured: 'źródło nieustawione',
};

const TYPE_LABEL: Record<Insight['type'], string> = {
  TOP_DISH: 'Największa wartość katalogowa',
  FASTEST_GROWING_DISH: 'Największy wzrost',
  FASTEST_DECLINING_DISH: 'Największy spadek',
  LOW_PERFORMER: 'Słaby wynik',
  PRICE_CHANGE_SIGNAL: 'Sygnał cenowy',
  HIGH_REPEAT_PRODUCT: 'Wysoka powtarzalność',
  SCHOOL_OUTLIER: 'Odstępstwo szkolne',
};

// Klucze dowodów przychodzą z silnika po angielsku (są częścią kontraktu API i mają takie zostać).
// Operator czyta po polsku — tłumaczenie jest wyłącznie warstwą prezentacji.
const EVIDENCE_LABEL: Record<string, string> = {
  units: 'pozycji sprzedanych',
  units_current: 'pozycji w oknie bieżącym',
  units_previous: 'pozycji w oknie porównawczym',
  orders: 'zamówień',
  buyers: 'kupujących',
  repeat_buyers: 'kupujących powracających',
  days: 'dni ze sprzedażą',
  schools: 'szkół',
  restaurants: 'restauracji',
  revenue_list_grosze: 'wartość katalogowa (grosze)',
  school_total_units: 'pozycji w tej szkole (łącznie)',
  other_schools: 'porównywanych szkół',
  unit_price_current_grosze: 'średnia cena teraz (grosze)',
  unit_price_previous_grosze: 'średnia cena wcześniej (grosze)',
};

function EvidenceRow({ label, value }: { label: string; value: number | null }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', py: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value === null || value === undefined ? '—' : value.toLocaleString('pl-PL')}
      </Typography>
    </Stack>
  );
}

/**
 * Insight pokazany w pełnym kontrakcie: FAKT → DLACZEGO WAŻNE → CO ZROBIĆ → NA CZYM STOI.
 * Sekcja „na czym stoi” nie jest zwijana ani schowana — bez niej rekomendacja jest wróżeniem.
 */
/**
 * @param kandydaci wartości ZADEKLAROWANEJ metryki rankingu dla wszystkich bytów zbioru
 *   źródłowego. Bez nich karta potrafi wykryć brak deklaracji, ale nie sprzeczność z danymi —
 *   i mówi o tym wprost, zamiast milcząco zaliczać insight jako sprawdzony.
 */
export function InsightCard({
  insight,
  kandydaci,
  kartaId,
}: {
  insight: Insight;
  kandydaci?: readonly KandydatSuperlatywu[];
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. Ta karta jest tego skrajnym
   * przypadkiem — renderuje się w pętli po sygnałach, więc „Na czym to stoi" i „Dlaczego
   * to ważne" występują tyle razy, ile insightów zwrócił silnik. Po tekście nie da się
   * wskazać KTÓREJ karty dotyczy sprawdzenie.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  const opisZrodla = useOpisZrodla();
  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" label={TYPE_LABEL[insight.type] ?? insight.type} />
        <DataQualityBadge quality={insight.data_quality} />
        {/*
          KLASYFIKACJA ŹRÓDŁA PRZY KAŻDYM INSIGHCIE, nie tylko w pasku aplikacji.
          Powód: karta insightu bywa oglądana w oderwaniu od reszty ekranu — na zrzucie,
          we wklejce, na rzutniku. „TOP DANIE: X" bez informacji, że policzono to na zbiorze
          kontrolowanym, czyta się jak wniosek z produkcji. Znacznik jakości mówi, ILE dane są
          warte; ten mówi, SKĄD są. To dwie różne rzeczy i obie muszą jechać razem z liczbą.
        */}
        <Chip
          size="small"
          variant="outlined"
          color={config.dataOrigin === 'production' ? 'default' : 'warning'}
          label={SOURCE_LABEL[config.dataOrigin]}
          title={opisZrodla}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          próba: {insight.sample_size.toLocaleString('pl-PL')}
        </Typography>
      </Stack>

      <Typography variant="h6" sx={{ mb: 1 }}>
        {insight.title}
      </Typography>

      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {insight.fact}
      </Typography>

      {/*
        STRAŻNIK SUPERLATYWU. Zdanie „największy/najwyższy" jest twierdzeniem o SKRAJNOŚCI
        w zbiorze — a przy próbie mniejszej niż dwa byty nie ma zbioru, w którym można być
        skrajnym. Przegląd konceptu R5-15-009 wykrył insight ogłaszający „największy spadek
        w całej sieci" dla bytu, który nim nie był; frontend nie może poprawić liczby przychodzącej
        z backendu, ale jest ostatnim miejscem, w którym da się złapać zdanie sprzeczne
        z załączonym do niego dowodem.

        GRANICA TEJ KONTROLI, powiedziana wprost: sprawdzamy tylko warunek konieczny (próba ≥ 2).
        Pełne sprawdzenie „czy TEN byt jest skrajny" wymaga, żeby backend deklarował, NA KTÓREJ
        metryce superlatyw rankinguje — dziś tego pola nie ma. Logika porównania jest gotowa
        i przetestowana w lib/superlatyw.ts; brakuje wyłącznie deklaracji po stronie źródła.
      */}
      {(() => {
        const ocena = ocenInsight(insight, kandydaci ?? []);
        if (ocena.ocena === 'NIE_DOTYCZY') return null;

        if (ocena.ocena === 'POTWIERDZONY') {
          // Potwierdzenie też jest informacją — mówi, WEDŁUG CZEGO zdanie jest prawdziwe.
          return (
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--sukces)', mb: 1 }}>
              Skrajność sprawdzona wobec zadeklarowanej metryki: {ocena.metryka}
            </Typography>
          );
        }

        return (
          <Alert severity={ocena.ocena === 'SPRZECZNY' ? 'error' : 'warning'} sx={{ mt: 1, mb: 1 }}>
            <Typography variant="body2">
              {ocena.ocena === 'SPRZECZNY'
                ? `Twierdzenie o skrajności NIE zgadza się ze zbiorem: ${ocena.powod}`
                : ocena.powod}
            </Typography>
          </Alert>
        );
      })()}

      {insight.sample_size < 2 ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Próba wynosi {insight.sample_size}. Przy jednym bycie nie ma zbioru, w którym można być
          skrajnym — potraktuj to zdanie jako obserwację o pojedynczym przypadku, nie jako rekord.
        </Alert>
      ) : null}

      <Typography variant="overline" color="text.secondary">
        Dlaczego to ważne
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {insight.why_it_matters}
      </Typography>

      <Typography variant="overline" color="text.secondary">
        Rekomendowany następny krok
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {insight.recommended_next_step}
      </Typography>

      {insight.hypothesis ? (
        <>
          <Typography variant="overline" color="text.secondary">
            Hipoteza
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {insight.hypothesis}
          </Typography>
        </>
      ) : null}

      {insight.suggested_test ? (
        <>
          <Typography variant="overline" color="text.secondary">
            Proponowany test
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {insight.suggested_test}
          </Typography>
        </>
      ) : null}

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="overline" color="text.secondary">
        Na czym to stoi
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        {Object.entries(insight.evidence).map(([key, value]) => (
          <EvidenceRow key={key} label={EVIDENCE_LABEL[key] ?? key} value={value} />
        ))}
      </Box>

      {/*
        PODSTAWA METRYKI W DOWODACH, nie tylko w nagłówku ekranu.
        Karta insightu ma być samowystarczalna: ktoś, kto ją zobaczy na zrzucie, musi wiedzieć
        nie tylko ILE i SKĄD, ale też CO dokładnie policzono. Bez tego „Największa wartość
        katalogowa" bywa czytane jako „największy przychód" — a to inna liczba i inna decyzja.
        Wolumen: silnik liczy pozycje (COUNT(*) wierszy pozycji), bo ilość bywa NULL-em.
      */}
      <Box sx={{ mt: 1.5 }}>
        <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_ITEM_ROWS', time: 'DELIVERY_DATE' }} />
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Okno: {formatRange(insight.time_window.date_from, insight.time_window.date_to)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Porównanie: {formatRange(insight.comparison_window.date_from, insight.comparison_window.date_to)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Silnik: {insight.engine_version}
        </Typography>
      </Stack>

      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
        Związek przyczynowy nieustalony — to jest współwystępowanie, nie dowód.
      </Typography>
    </Paper>
  );
}
